<template>
  <div class="lobby-menu-container">
    <!-- Menu Toggle Button -->
    <button
      @click.stop="toggleMenu"
      class="lobby-menu-button"
      :class="{ active: isOpen }"
      aria-label="Lobby menu"
      aria-haspopup="true"
      :aria-expanded="isOpen"
      data-tutorial="lobby-menu"
    >
      <span class="material-symbols-outlined">more_vert</span>
    </button>

    <!-- Dropdown Menu -->
    <div
      v-show="isOpen"
      class="lobby-menu-dropdown"
      role="menu"
      aria-label="Lobby menu options"
    >
      <!-- General Actions Section -->
      <div class="menu-section">
        <button @click="handleAction('profile')" class="menu-item" role="menuitem">
          <span class="material-symbols-outlined">person</span>
          <span>Profile</span>
        </button>

        <button @click="handleAction('how-to-play')" class="menu-item" role="menuitem">
          <span class="material-symbols-outlined">help</span>
          <span>How to Play</span>
        </button>

        <button @click="handleAction('interface-guide')" class="menu-item" role="menuitem">
          <span class="material-symbols-outlined">school</span>
          <span>Interface Guide</span>
        </button>

        <div class="menu-divider"></div>

        <button
          @click="handleAction('logout')"
          class="menu-item danger"
          role="menuitem"
        >
          <span class="material-symbols-outlined">logout</span>
          <span>Logout</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

/**
 * LobbyMenu Component
 *
 * Dropdown menu for lobby screens with logout and utility actions.
 * Matches the styling and behavior of GameMenu.vue for consistency.
 *
 * Features:
 * - How to Play link
 * - Logout action
 * - Click outside to close
 * - Keyboard navigation (ESC to close)
 *
 * @example
 * <LobbyMenu
 *   @logout="handleLogout"
 *   @how-to-play="showHowToPlay"
 * />
 */

const emit = defineEmits<{
  'logout': []
  'how-to-play': []
  'interface-guide': []
  'profile': []
}>()

/**
 * Whether menu dropdown is open
 */
const isOpen = ref(false)

/**
 * Toggle menu dropdown
 */
function toggleMenu(): void {
  isOpen.value = !isOpen.value
}

/**
 * Close menu dropdown
 */
function closeMenu(): void {
  isOpen.value = false
}

/**
 * Handle menu item click
 */
function handleAction(action: string): void {
  closeMenu()

  switch (action) {
    case 'logout':
      emit('logout')
      break
    case 'how-to-play':
      emit('how-to-play')
      break
    case 'interface-guide':
      emit('interface-guide')
      break
    case 'profile':
      emit('profile')
      break
  }
}

/**
 * Handle ESC key press
 */
function handleKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && isOpen.value) {
    closeMenu()
  }
}

/**
 * Handle clicks outside the menu to close it
 */
function handleDocumentClick(event: MouseEvent): void {
  const target = event.target as HTMLElement
  if (isOpen.value && !target.closest('.lobby-menu-container')) {
    closeMenu()
  }
}

// Keyboard and click-outside listeners
onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
  document.addEventListener('click', handleDocumentClick)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('click', handleDocumentClick)
})
</script>

<style scoped>
/* ============================================================================
   Container
   ============================================================================ */

.lobby-menu-container {
  position: relative;
  display: inline-block;
  z-index: 10000;
}

/* ============================================================================
   Menu Button
   ============================================================================ */

.lobby-menu-button {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(201, 162, 39, 0.9) 0%, rgba(181, 142, 29, 0.9) 100%);
  border: 2px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1a2f3f;
  transition: all 0.2s;
  position: relative;
  z-index: 10;
}

.lobby-menu-button:hover {
  background: linear-gradient(135deg, rgba(26, 47, 63, 0.95) 0%, rgba(39, 63, 82, 0.95) 100%);
  border-color: rgba(232, 197, 71, 0.4);
  color: rgba(232, 197, 71, 0.9);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
  transform: translateY(-2px);
}

.lobby-menu-button.active {
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.2) 0%, rgba(212, 175, 55, 0.2) 100%);
  border-color: rgba(232, 197, 71, 0.6);
  color: #e8c547;
}

.lobby-menu-button .material-symbols-outlined {
  font-size: 1.5rem;
}

/* ============================================================================
   Dropdown Menu
   ============================================================================ */

.lobby-menu-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 200px;
  background: linear-gradient(135deg, rgba(13, 31, 45, 0.98) 0%, rgba(26, 47, 63, 0.98) 100%);
  border-radius: 16px;
  box-shadow:
    0 16px 48px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(255, 255, 255, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  padding: 0.75rem;
  z-index: 99999;
  overflow: hidden;
}

/* Glowing border effect */
.lobby-menu-dropdown::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: 16px;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(232, 197, 71, 0.3),
    rgba(232, 197, 71, 0.05),
    rgba(232, 197, 71, 0.3)
  );
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: 0.5;
  pointer-events: none;
}

/* ============================================================================
   Menu Sections
   ============================================================================ */

.menu-section {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.menu-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0.5rem 0;
}

/* ============================================================================
   Menu Items
   ============================================================================ */

.menu-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9375rem;
  font-weight: 500;
  text-align: left;
  width: 100%;
  position: relative;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.menu-item:active {
  background: rgba(255, 255, 255, 0.05);
  transform: scale(0.98);
}

.menu-item .material-symbols-outlined {
  font-size: 1.25rem;
  color: rgba(232, 197, 71, 0.8);
  flex-shrink: 0;
}

/* Danger items (destructive actions) */
.menu-item.danger .material-symbols-outlined {
  color: rgba(239, 68, 68, 0.8);
}

.menu-item.danger:hover {
  background: rgba(239, 68, 68, 0.1);
  border-left: 3px solid rgba(239, 68, 68, 0.5);
  padding-left: calc(1rem - 3px);
}

/* ============================================================================
   Responsive
   ============================================================================ */

@media (max-width: 640px) {
  .lobby-menu-dropdown {
    min-width: 180px;
    right: -8px;
  }

  .menu-item {
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
  }

  .menu-item .material-symbols-outlined {
    font-size: 1.125rem;
  }
}

@media (max-width: 768px) {
  .lobby-menu-button {
    width: 44px;
    height: 44px;
  }

  .lobby-menu-button .material-symbols-outlined {
    font-size: 1.375rem;
  }
}
</style>
